import { useEffect, useRef, useState } from 'react';

import { Label } from '@eneco/dc/client/types';
import { DC_Repositories_Base_Enumerations_BusinessUnit } from '@monorepo-types/dc';
import { type Fields } from '@sitecore/types/OfferStep';

import { FlowConsumerContextEnergy } from '../../../../context';
import {
  checkAvailabilityOfferType,
  checkOffersIncludesDynamicPricingProduct,
  getProductFilterForOffer,
} from '../../../../shared/helpers';
import { handleNbaFeedback } from '../../../../shared/helpers/dc';
import { useFlowTracking } from '../../../../shared/hooks';
import { type Offer, type OfferResponse, type OfferMachineEvent, type ProductFilter } from '../../../../shared/types';

interface MachineConfig {
  eventType: 'GET_OFFER' | 'GET_EXTENSION_OFFER';
  payload?: Record<string, unknown>;
}

interface MountConfig {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onMount?: (sendToFlowMachine: (event: any) => void) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  sendToFlowMachine: (event: any) => void;
}

interface FormActions {
  setValue: (name: 'discountCode', value: string) => void;
  setProductFilter: (value: ProductFilter) => void;
}

interface UseOfferFetchingParams {
  businessUnit: DC_Repositories_Base_Enumerations_BusinessUnit;
  label: Label;
  flowContext: FlowConsumerContextEnergy;
  discountCode?: string;
  offers: Offer[];
  offer?: OfferResponse;
  offerState: {
    matches: (state: string) => boolean;
  };
  sendToOfferMachine: (event: OfferMachineEvent) => void;
  textualData?: Fields;
  isPreSelectionEnabled: boolean;
  machineConfig: MachineConfig;
  mountConfig: MountConfig;
  formActions: FormActions;
}

const NBA_FEEDBACK_STATUSES = ['Served', 'Viewed'] as const;

export function useOfferFetching({
  businessUnit,
  label,
  flowContext,
  discountCode,
  offers,
  offer,
  offerState,
  sendToOfferMachine,
  textualData,
  isPreSelectionEnabled,
  machineConfig,
  mountConfig,
  formActions,
}: UseOfferFetchingParams) {
  const isExtensionFlow = machineConfig.eventType === 'GET_EXTENSION_OFFER';
  const loadingStateMatcher = isExtensionFlow ? 'GET_EXTENSION' : 'FETCHING';

  const { setValue, setProductFilter } = formActions;
  const { onMount: mountOnMount, sendToFlowMachine: mountSendToFlowMachine } = mountConfig;

  const { trackViewItem } = useFlowTracking();
  const trackViewItemRef = useRef(trackViewItem);
  trackViewItemRef.current = trackViewItem;

  const [isLoading, setIsLoading] = useState(isExtensionFlow);
  const [isSuccessful, setIsSuccessful] = useState(!isExtensionFlow);
  const [isFailed, setIsFailed] = useState(false);
  const [isNotFound, setIsNotFound] = useState(false);
  const [isNotAvailable, setIsNotAvailable] = useState(false);
  const [isNotAuthenticated, setIsNotAuthenticated] = useState(false);
  const [shouldShowMoreInfoDialog, setShouldShowMoreInfoDialog] = useState(false);

  // Mount effect: fetch offers + optional custom side effects
  useEffect(() => {
    mountOnMount?.(mountSendToFlowMachine);
    sendToOfferMachine({
      type: machineConfig.eventType,
      flowContext: { ...flowContext },
      ...machineConfig.payload,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional mount-only effect
  }, []);

  // Offer state machine sync
  useEffect(() => {
    if (!offerState.matches('IDLE')) {
      setIsLoading(offerState.matches(loadingStateMatcher));
      setIsFailed(!offerState.matches(loadingStateMatcher) && !offerState.matches('SUCCESS'));
      setIsSuccessful(offerState.matches('SUCCESS'));

      setIsNotFound(offerState.matches('ERROR_NOT_FOUND'));
      setIsNotAvailable(offerState.matches('ERROR_NOT_AVAILABLE'));
      setIsNotAuthenticated(offerState.matches('ERROR_NOT_AUTHENTICATED'));
    }

    if (offerState.matches('SUCCESS')) {
      if (isPreSelectionEnabled) {
        const selectedOfferType = checkAvailabilityOfferType(offers, discountCode);

        if (selectedOfferType) {
          const selectedOffer = offers.find(o => o.type === selectedOfferType);

          setValue('discountCode', selectedOfferType);
          setProductFilter(getProductFilterForOffer(selectedOffer));
        }
      }

      setShouldShowMoreInfoDialog(
        !!(
          checkOffersIncludesDynamicPricingProduct(offers) && textualData?.content?.moreInfoDialog?.value?.triggerText
        ),
      );

      offers.forEach(availableOffer => {
        trackViewItemRef.current(availableOffer, offer);
        if (availableOffer.nbaContext?.servingPointId) {
          for (const status of NBA_FEEDBACK_STATUSES) {
            handleNbaFeedback(
              businessUnit,
              label,
              status,
              availableOffer.nbaContext.serviceLocationId ?? '',
              availableOffer.nbaContext.contextId ?? '',
              availableOffer.nbaContext.actionId,
              availableOffer.nbaContext.variationId,
              availableOffer.nbaContext.servingPointId,
            );
          }
        }
      });
    }
  }, [
    offerState,
    loadingStateMatcher,
    isPreSelectionEnabled,
    offers,
    discountCode,
    textualData,
    offer,
    businessUnit,
    label,
    setValue,
    setProductFilter,
  ]);

  return {
    isLoading,
    isSuccessful,
    isFailed,
    isNotFound,
    isNotAvailable,
    isNotAuthenticated,
    shouldShowMoreInfoDialog,
  };
}
